// src/components/DetailedView.jsx
import { useEffect, useMemo, useState } from "react";
import "./DetailedView.css";
import { getGenreName } from "../../services/genreService"; // adjust path if needed

const IMG = "https://image.tmdb.org/t/p";

export default function DetailedView({
                                         open,
                                         movie,              // { id, title, poster_path, backdrop_path, overview, vote_average, release_date, genre_ids? }
                                         onClose = () => {},
                                         apiKey,             // pass API key from parent
                                         bearer,             // pass Bearer token from parent
                                     }) {
    const [activeId, setActiveId] = useState(movie?.id || null);
    const [details, setDetails] = useState(null);
    const [similar, setSimilar] = useState([]);
    const [loading, setLoading] = useState(false);
    const [simLoading, setSimLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;
        setActiveId(movie?.id || null);
    }, [open, movie?.id]);

    // fetch full details for activeId
    useEffect(() => {
        if (!open || !activeId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError("");
            try {
                const res = await fetch(
                    `https://api.themoviedb.org/3/movie/${activeId}?api_key=${apiKey}&append_to_response=videos,credits`,
                    {
                        headers: {
                            Authorization: `Bearer ${bearer}`,
                            "Content-Type": "application/json;charset=utf-8",
                        },
                    }
                );
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (!cancelled) setDetails(data);
            } catch (e) {
                if (!cancelled) setError(e.message || "Failed to load details");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, activeId, apiKey, bearer]);

    // fetch similar for activeId
    useEffect(() => {
        if (!open || !activeId) return;
        let cancelled = false;
        (async () => {
            setSimLoading(true);
            try {
                const res = await fetch(
                    `https://api.themoviedb.org/3/movie/${activeId}/similar?api_key=${apiKey}&page=1`,
                    {
                        headers: { Authorization: `Bearer ${bearer}` },
                    }
                );
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (!cancelled) setSimilar(data.results || []);
            } catch {
                if (!cancelled) setSimilar([]);
            } finally {
                if (!cancelled) setSimLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, activeId, apiKey, bearer]);

    const title = details?.title || movie?.title || movie?.name || "Untitled";
    const year = (details?.release_date || movie?.release_date || "").slice(0, 4);
    const rating =
        typeof (details?.vote_average ?? movie?.vote_average) === "number"
            ? (details?.vote_average ?? movie?.vote_average).toFixed(1)
            : "—";

    const poster = useMemo(() => {
        const p = details?.poster_path ?? movie?.poster_path ?? null;
        return p ? `${IMG}/w500${p}` : null;
    }, [details, movie]);

    const backdrop = useMemo(() => {
        const b = details?.backdrop_path ?? movie?.backdrop_path ?? null;
        return b ? `${IMG}/w1280${b}` : null;
    }, [details, movie]);

    const genres = useMemo(() => {
        // prefer full details.genres (array of {id,name}); else map ids via service
        if (details?.genres?.length) return details.genres.map((g) => g.name);
        const ids = details?.genre_ids || movie?.genre_ids || [];
        return ids.map((id) => getGenreName(id)).filter(Boolean);
    }, [details, movie]);

    if (!open) return null;

    return (
        <div className="dv-modal" role="dialog" aria-modal="true" onClick={onClose}>
            <div className="dv-panel" onClick={(e) => e.stopPropagation()}>


                {/* Backdrop banner */}
                <div className="dv-hero" style={{ backgroundImage: backdrop ? `url(${backdrop})` : "none" }}>
                    <button className="dv-close" onClick={onClose} aria-label="Close">✕</button>
                    <div className="dv-hero__scrim" />
                    <div className="dv-hero__content">
                        {poster ? (
                            <img className="dv-poster" src={poster} alt={`${title} poster`} loading="lazy" />
                        ) : (
                            <div className="dv-poster dv-poster--ph">No Poster</div>
                        )}

                        <div className="dv-meta">
                            <div className="dv-badges">
                <span className="dv-badge" title="Rating">
                  ★ {rating}
                </span>
                                {genres?.length ? (
                                    <span className="dv-badge dv-badge--muted">
                    {genres.join(" · ")}
                  </span>
                                ) : null}
                                {details?.runtime ? (
                                    <span className="dv-badge dv-badge--muted">{details.runtime} min</span>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="dv-body">
                    {loading ? (
                        <div className="dv-state">Loading…</div>
                    ) : error ? (
                        <div className="dv-state dv-state--err">⚠️ {error}</div>
                    ) : (
                        <>
                            <h2 className="dv-title">
                                {title} {year ? <span className="dv-year">({year})</span> : null}
                            </h2>
                            {details?.overview ? (
                                <p className="dv-overview">{details.overview}</p>
                            ) : movie?.overview ? (
                                <p className="dv-overview">{movie.overview}</p>
                            ) : (
                                <p className="dv-overview dv-overview--muted">No overview available.</p>
                            )}

                            {/* Optional: cast snippet */}
                            {details?.credits?.cast?.length ? (
                                <div className="dv-cast">
                                    <h4 className="dv-sectionTitle">Top cast</h4>
                                    <div className="dv-castList">
                                        {details.credits.cast.slice(0, 10).map((c) => (
                                            <div key={c.cast_id || `${c.id}-${c.credit_id}`} className="dv-castItem">
                                                <div className="dv-castName">{c.name}</div>
                                                <div className="dv-castRole">{c.character}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                        </>
                    )}
                </div>

                {/* Similar movies rail */}
                <div className="dv-similar">
                    <div className="dv-similar__head">
                        <h4 className="dv-sectionTitle">Similar</h4>
                        {simLoading && <span className="dv-similar__meta">Loading…</span>}
                    </div>
                    <div className="dv-similar__rail">
                        {similar.map((m) => {
                            const p = m.poster_path ? `${IMG}/w185${m.poster_path}` : null;
                            return (
                                <button
                                    key={m.id}
                                    className="dv-thumb"
                                    title={m.title || m.name}
                                    onClick={() => setActiveId(m.id)}
                                >
                                    {p ? (
                                        <img src={p} alt={m.title || m.name} loading="lazy" />
                                    ) : (
                                        <div className="dv-thumb__ph">{m.title}</div>
                                    )}
                                </button>
                            );
                        })}
                        {!similar.length && !simLoading ? (
                            <div className="dv-similar__meta">No similar titles.</div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
